import React, { Component } from 'react'
import ReactECharts from 'echarts-for-react'
import PropTypes from 'prop-types'
import * as R from 'ramda'
import setFormat from '../helpers'

export default class Bar extends Component {
    defaultColors = [
        '#102E4A',
        '#33E1D5',
        '#2B3C44',
        '#725AFF',
        '#AA9CFF',
        '#D8D3F5',
        '#AFAACA',
        '#7B75A1',
    ]

    FILL_BORDER_MAP = {
        '#102E4A': '#4FFFD6',
        '#33E1D5': '#02A499',
        '#2B3C44': '#1C2F37',
        '#725AFF': '#1900AD',
        '#AA9CFF': '#1900AD',
        '#D8D3F5': '#230E9F',
        '#AFAACA': '#423974',
        '#7B75A1': '#423B6B',
        '#0DAAC9': '#006B80',
        '#B864DF': '#720BA2',
        '#FC73AC': '#AD0048',
        '#FFAACF': '#AD004B',
        '#4FFFD6': '#00AD85',
        '#9BFFFD': '#00ADAA',
        '#A1E0FF': '#0074AD',
        '#DAB3E8': '#741895',
        '#F2B0F5': '#A600AD',
        '#FF9B9C': '#AD0002',
    }

    series = {
        name: '',
        type: 'bar',
        stack: 'data',
        label: {
            show: false,
        },
        emphasis: {
            focus: 'series',
        },
        data: [],
    }

    constructor(props) {
        super(props)
        // Seed the highlight set from the prop so bars can start highlighted,
        // then let clicks take over (see componentDidUpdate for parent resets).
        this.state = {
            highlighted: Bar.toHighlightSet(props.initialHighlighted, props.highlightMode),
        }
    }

    // Allow the parent to reset the highlight later by changing the prop's
    // identity. IMPORTANT: pass a *stable* reference (e.g. parent state), NOT
    // a fresh literal like initialHighlighted={[]} - a new array every render
    // makes this fire every render and wipe the user's clicks.
    componentDidUpdate(prevProps) {
        const { initialHighlighted, highlightMode } = this.props
        if (initialHighlighted !== prevProps.initialHighlighted) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({ highlighted: Bar.toHighlightSet(initialHighlighted, highlightMode) })
        }
    }

    // In 'background' mode a whole stacked column (every series at a category)
    // is highlighted as one unit, so we bucket the persisted optionIds under a
    // single synthetic series key and key the highlight Set by category index
    // alone.
    static WHOLE_STACK_SERIES = '__wholeStack__'

    // A bar is identified by its series name + category index in 'border' mode.
    // In 'background' mode the whole column is highlighted, so the key is the
    // category index alone (series-agnostic).
    static highlightKey(seriesName, index, mode) {
        return mode === 'background' ? `${index}` : `${seriesName}:${index}`
    }

    static toHighlightSet(list, mode) {
        return new Set((list || []).map(({ series, index }) => Bar.highlightKey(series, index, mode)))
    }

    handleBarClick = (params) => {
        const { highlightable, onHighlightChange, highlightMode } = this.props
        if (!highlightable) return
        const key = Bar.highlightKey(params.seriesName, params.dataIndex, highlightMode)
        this.setState(
            (prev) => {
                const highlighted = new Set(prev.highlighted)
                if (highlighted.has(key)) highlighted.delete(key) // highlighted -> clear
                else highlighted.add(key) // un-highlighted -> highlight
                return { highlighted }
            },
            // side-effects belong in the setState callback, not inside the updater
            () => {
                if (typeof onHighlightChange === 'function') {
                    onHighlightChange(
                        [...this.state.highlighted].map((k) => {
                            if (highlightMode === 'background') {
                                return { series: Bar.WHOLE_STACK_SERIES, index: Number(k) }
                            }
                            const [series, index] = k.split(':')
                            return { series, index: Number(index) }
                        }),
                    )
                }
            },
        )
    }

    // Resolve the fill a given bar actually renders with, checking (in order)
    // the per-datum colour, the per-series colour, then the palette by series
    // index. This reads the *resolved* option, so it works no matter how the
    // colour was set - including via layoutEdit, which runs before this.
    resolveFill = (opt, seriesIndex, datum) => {
        if (datum && typeof datum === 'object' && datum.itemStyle && datum.itemStyle.color) {
            return datum.itemStyle.color
        }
        const series = opt.series && opt.series[seriesIndex]
        if (series && series.itemStyle && series.itemStyle.color) {
            return series.itemStyle.color
        }
        if (Array.isArray(opt.color) && opt.color.length) {
            return opt.color[seriesIndex % opt.color.length]
        }
        return undefined
    }

    // Runs LAST, after transpose + layoutEdit, so every series' colour is
    // already resolved on the option. For each highlighted bar it looks up the
    // border from FILL_BORDER_MAP using that bar's resolved fill and attaches
    // it. The per-datum itemStyle merges over the series itemStyle, so the
    // borderRadius on the last series is preserved.
    applyHighlightBorders = (opt) => {
        const { highlightable, highlightBorderWidth, highlightBorderColor } = this.props
        const { highlighted } = this.state
        if (!highlightable || !highlighted.size || !Array.isArray(opt.series)) return opt

        opt.series = opt.series.map((series, seriesIndex) => {
            if (!series || !Array.isArray(series.data)) return series
            const data = series.data.map((datum, index) => {
                if (!highlighted.has(Bar.highlightKey(series.name, index))) return datum
                const fill = this.resolveFill(opt, seriesIndex, datum)
                const borderColor = this.FILL_BORDER_MAP[(fill || '').toUpperCase()]
                    || highlightBorderColor
                const base = (datum !== null && typeof datum === 'object')
                    ? datum
                    : { value: datum }
                return {
                    ...base,
                    itemStyle: {
                        ...(base.itemStyle || {}),
                        borderColor,
                        borderWidth: highlightBorderWidth,
                    },
                }
            })
            return { ...series, data }
        })
        return opt
    }

    // 'background' highlight mode: instead of bordering a single segment, draw a
    // grey band behind the whole column (every stacked series at a category).
    // Uses an ECharts markArea on the first series, which renders behind the
    // bars and auto-spans the full value axis. The category axis is yAxis once
    // transposed (horizontal stacked bars), xAxis otherwise.
    applyHighlightBackgrounds = (opt) => {
        const {
            highlightable, highlightBackgroundColor, transpose, xAxisLabels,
        } = this.props
        const { highlighted } = this.state
        if (!highlightable || !highlighted.size || !Array.isArray(opt.series) || !opt.series.length) {
            return opt
        }

        const catAxisKey = transpose ? 'yAxis' : 'xAxis'
        const areas = [...highlighted]
            .map((k) => Number(k))
            .filter((i) => Number.isInteger(i) && i >= 0 && i < xAxisLabels.length)
            .map((i) => ([
                { [catAxisKey]: xAxisLabels[i] },
                { [catAxisKey]: xAxisLabels[i] },
            ]))
        if (!areas.length) return opt

        const [first, ...rest] = opt.series
        const markArea = {
            silent: true,
            itemStyle: { color: highlightBackgroundColor },
            data: areas,
        }
        return { ...opt, series: [{ ...first, markArea }, ...rest] }
    }

    options = () => ({
        grid: {
            containLabel: true,
        },
        tooltip: {
            trigger: 'item',
            formatter: (params) => (
                `<div style="color:white;background-color:#102E4A;padding:7px 9px;font-size:15px;">${R.pathOr('', ['xAxisLabels', params.dataIndex], this.props)}</div>
                <div style="padding:10px 15px;font-size:14px;color:#102E4A;display:flex;align-items:center;flex-direction:row;">
                    ${params.marker}
                    ${params.seriesName}
                    <span style="margin-left:12px;font-weight:800;">
                        ${setFormat(params.value, this.props.dataFormat, this.props.tooltipDecimals)} 
                    </span>
                </div>`
            ),
            extraCssText: 'min-width: 120px;border-radius: 4px;overflow:hidden;',
            padding: 0,
            borderWidth: 0,
            className: 'barTooltip',
        },
        color: this.props.colors || this.defaultColors,
        xAxis: {
            type: 'category',
            data: [],
            axisPointer: {
                type: 'shadow',
            },
            axisLabel: {
                fontSize: 14,
                color: 'black',
            },
            axisLine: {
                show: true,
                lineStyle: {
                    color: '#E2E6E9',
                },
            },
        },
        yAxis: {
            type: 'value',
            name: '',
            axisLabel: {
                fontSize: 14,
                formatter: (value) => setFormat(value, this.props.dataFormat, this.props.yAxisDecimals),
                color: '#8B99A7',
            },
            splitLine: {
                show: true,
            },
            axisLine: {
                show: true,
                lineStyle: {
                    color: '#E2E6E9',
                },
            },
        },
        series: [
        ],
    })

    layoutEdit = (layout) => {
        const { layoutEdit } = this.props
        if (!layoutEdit || !layoutEdit.length) return layout
        return layoutEdit.reduce(
            (newLayout, { path, value }) => (
                R.set(R.lensPath(path), value, newLayout)
            ),
            layout,
        )
    }

    transposeAxis = (chartOptions) => {
        const { transpose } = this.props
        if (!transpose) return chartOptions
        return { ...chartOptions, xAxis: chartOptions.yAxis, yAxis: chartOptions.xAxis }
    }

    chartOptions = () => {
        const {
            chartData, benchmarkData, xAxisLabels, defaultSeries,
            transpose, showLegend, legend, highlightable, highlightMode,
        } = this.props
        const chartOptions = R.clone(this.options())
        chartOptions.legend = { show: showLegend, ...legend }
        const series = defaultSeries || this.series
        chartOptions.xAxis.data = xAxisLabels
        const borderRadius = transpose ? [0, 2, 2, 0] : [2, 2, 0, 0]

        if (chartData && chartData.length) {
            chartData.forEach(({ data, name }, index) => {
                const itemStyle = {}
                if (index === (chartData.length - 1)) itemStyle.borderRadius = borderRadius
                chartOptions.series.push({
                    ...series, data, name, itemStyle,
                })
            })
        }
        if (benchmarkData && benchmarkData.length) {
            benchmarkData.forEach(({ data, name }, index) => {
                const itemStyle = {}
                if (index === (chartData.length - 1)) itemStyle.borderRadius = borderRadius
                chartOptions.series.push({
                    ...series, data, name, stack: 'benchmark', itemStyle,
                })
            })
        }

        // Order matters: transpose -> layoutEdit (sets colours) -> highlight
        // borders (reads the now-resolved colours). Highlighting must be last.
        let opt = this.transposeAxis(chartOptions)
        opt = this.layoutEdit(opt)
        // When highlighting is off, disable ECharts' native selection (which
        // layoutEdit may have enabled via selectedMode) so bars aren't clickable.
        if (!highlightable && Array.isArray(opt.series)) {
            opt.series = opt.series.map((s) => (s ? { ...s, selectedMode: false } : s))
        }
        opt = highlightMode === 'background'
            ? this.applyHighlightBackgrounds(opt)
            : this.applyHighlightBorders(opt)
        return opt
    }

    render() {
        const { style, setRef } = this.props

        return (
            <div className="barChart" style={{ height: '400px', width: '500px', ...style }}>
                <ReactECharts
                    option={this.chartOptions()}
                    className="chart"
                    style={{ height: '100%' }}
                    onEvents={{ click: this.handleBarClick }}
                    notMerge
                    ref={(e) => { if (setRef && typeof setRef === 'function') setRef(e) }}
                />
            </div>
        )
    }
}

Bar.propTypes = {
    layoutEdit: PropTypes.arrayOf(
        PropTypes.shape({
            path: PropTypes.arrayOf(PropTypes.string),
            value: PropTypes.string,
        }),
    ),
    style: PropTypes.object,
    xAxisLabels: PropTypes.arrayOf(PropTypes.string),
    chartData: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string,
            data: PropTypes.arrayOf(PropTypes.number),
        }),
    ),
    benchmarkData: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string,
            data: PropTypes.arrayOf(PropTypes.number),
        }),
    ),
    transpose: PropTypes.bool,
    defaultSeries: PropTypes.object,
    colors: PropTypes.arrayOf(PropTypes.string),
    legend: PropTypes.object,
    showLegend: PropTypes.bool,
    dataFormat: PropTypes.string,
    tooltipDecimals: PropTypes.number,
    yAxisDecimals: PropTypes.number,
    setRef: PropTypes.func,
    // --- highlighting ---
    highlightable: PropTypes.bool,
    initialHighlighted: PropTypes.arrayOf(
        PropTypes.shape({
            series: PropTypes.string,
            index: PropTypes.number,
        }),
    ),
    highlightBorderColor: PropTypes.string,
    highlightBorderWidth: PropTypes.number,
    // 'border' outlines a single clicked segment; 'background' shades the whole
    // column (all stacked series at a category) with a grey area behind it.
    highlightMode: PropTypes.oneOf(['border', 'background']),
    highlightBackgroundColor: PropTypes.string,
    onHighlightChange: PropTypes.func,
}

Bar.defaultProps = {
    layoutEdit: [],
    style: { height: '400px', width: '500px' },
    xAxisLabels: [],
    chartData: { name: [], data: [] },
    benchmarkData: undefined,
    transpose: false,
    defaultSeries: undefined,
    colors: undefined,
    legend: { bottom: 0, left: 50, icon: 'circle' },
    showLegend: false,
    dataFormat: undefined,
    tooltipDecimals: 0,
    yAxisDecimals: 0,
    setRef: undefined,
    // --- highlighting ---
    highlightable: false,
    initialHighlighted: [],
    highlightBorderColor: '#0A0A0A',
    highlightBorderWidth: 3,
    highlightMode: 'border',
    highlightBackgroundColor: '#EDF1F7',
}
